import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Search, Store, Loader2, AlertCircle } from 'lucide-react';

export default function AssignOutletsDialog({ open, onClose, recipeId, recipeName, onAssigned }) {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !recipeId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    // ── Fetch outlets — independent of recipe assignment fetch.
    // Spec: include all outlets where is_active === true (or undefined/missing);
    // do NOT filter by portfolio / manager / hierarchy / recipe assignment.
    const loadOutlets = axios
      .get(`${API}/outlets`, { headers: getHeaders() })
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const active = list.filter(o => o.is_active !== false);
        // eslint-disable-next-line no-console
        console.log('OUTLETS:', active);
        setOutlets(active);
      })
      .catch(err => {
        if (cancelled) return;
        setOutlets([]);
        setError('Failed to load outlets');
        // eslint-disable-next-line no-console
        console.error('Failed to fetch outlets', err);
      });

    // ── Fetch existing assignments — failure here MUST NOT clear outlets list.
    const loadAssigned = axios
      .get(`${API}/recipes/${recipeId}/outlets`, { headers: getHeaders() })
      .then(res => {
        if (cancelled) return;
        const items = res.data?.items || [];
        setSelected(new Set(items.map(o => o.id)));
      })
      .catch(() => {
        if (cancelled) return;
        setSelected(new Set());  // Safe default; user can still assign fresh.
      });

    Promise.all([loadOutlets, loadAssigned]).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [open, recipeId, API, getHeaders]);

  const filtered = useMemo(
    () => outlets.filter(o => !query.trim() || o.name.toLowerCase().includes(query.trim().toLowerCase())),
    [outlets, query],
  );

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const allVisibleSelected = filtered.length > 0 && filtered.every(o => selected.has(o.id));
  const toggleAllVisible = () => setSelected(s => {
    const n = new Set(s);
    if (allVisibleSelected) filtered.forEach(o => n.delete(o.id));
    else filtered.forEach(o => n.add(o.id));
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/recipes/${recipeId}/outlets`,
        { outlet_ids: [...selected] },
        { headers: getHeaders() });
      toast.success(`Assigned to ${selected.size} outlet${selected.size === 1 ? '' : 's'}`);
      onAssigned?.([...selected]);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Render the body — three explicit states ─────────────
  let body;
  if (loading) {
    body = (
      <p className="py-6 text-center text-xs text-[#5C6B64] flex items-center justify-center gap-2" data-testid="assign-outlets-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading outlets…
      </p>
    );
  } else if (error) {
    body = (
      <p className="py-6 text-center text-xs text-[#C45A41] flex items-center justify-center gap-2" data-testid="assign-outlets-error">
        <AlertCircle className="w-3.5 h-3.5" /> {error}
      </p>
    );
  } else if (outlets.length === 0) {
    body = (
      <p className="py-6 text-center text-xs text-[#5C6B64]" data-testid="assign-outlets-empty">
        No outlets found
      </p>
    );
  } else if (filtered.length === 0) {
    body = (
      <p className="py-6 text-center text-xs text-[#5C6B64]" data-testid="assign-outlets-no-match">
        No outlets match "{query}"
      </p>
    );
  } else {
    body = filtered.map(o => (
      <label key={o.id}
        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F9F9F8]"
        data-testid={`assign-outlet-row-${o.id}`}>
        <Checkbox checked={selected.has(o.id)}
          onCheckedChange={() => toggle(o.id)}
          data-testid={`assign-outlet-cb-${o.id}`} />
        <span className="flex-1 text-[#1A2421]">{o.name}</span>
      </label>
    ));
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-outlets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-4 h-4 text-[color:var(--color-primary)]" />
            Assign to Outlets
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5C6B64]">
            Select outlets that can see <strong>{recipeName || 'this recipe'}</strong> in their Manager view.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#5C6B64] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search outlets…"
              data-testid="assign-outlets-search"
              className="h-9 pl-8 text-sm" />
          </div>

          <div className="flex items-center justify-between px-1">
            <button type="button" onClick={toggleAllVisible}
              data-testid="assign-outlets-select-all"
              disabled={loading || filtered.length === 0}
              className="text-[11px] font-semibold text-[color:var(--color-primary)] hover:underline disabled:opacity-50 disabled:no-underline">
              {allVisibleSelected ? 'Clear visible' : 'Select all visible'}
            </button>
            <span className="text-[11px] text-[#5C6B64]" data-testid="assign-outlets-count">
              {selected.size} selected · {outlets.length} outlet{outlets.length === 1 ? '' : 's'} loaded
            </span>
          </div>

          <div className="max-h-64 overflow-y-auto border border-[#E2E8DF] rounded-md divide-y divide-[#E2E8DF]">
            {body}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-outlets-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading}
            className="bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
            data-testid="assign-outlets-save">
            {saving ? 'Saving…' : 'Save assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
